import type { CountryConfig, MarketCode } from "./types";
import { dkConfig } from "./dk";
import { noConfig } from "./no";
import store from "@/store";

export type { CountryConfig, MarketCode };

const configs: Record<MarketCode, CountryConfig> = {
  dk: dkConfig,
  no: noConfig,
};

export function getCountryConfig(market: MarketCode): CountryConfig {
  return configs[market] ?? dkConfig;
}

export function getMarketForInstitution(institution: {
  market?: string;
}): MarketCode {
  const market = institution?.market;
  if (market === "dk" || market === "no") return market;
  return "dk";
}

/**
 * Returns the current country config based on the active institution in the store.
 */
export function getCurrentCountryConfig(): CountryConfig {
  const institution = store.getters["institutions/getInstitution"] ?? {};
  return getCountryConfig(getMarketForInstitution(institution));
}

/**
 * Convenience: returns the market code for the current institution.
 */
export function getCurrentMarket(): MarketCode {
  const institution = store.getters["institutions/getInstitution"] ?? {};
  return getMarketForInstitution(institution);
}

export function isDanishMarket(): boolean {
  return getCurrentMarket() === "dk";
}

export function isNorwegianMarket(): boolean {
  return getCurrentMarket() === "no";
}

/**
 * Convenience: returns hoursPerDay for the current institution's market.
 */
export function getHoursPerDay(): number {
  return getCurrentCountryConfig().hoursPerDay;
}

/**
 * Convenience: returns hoursPerWeek for the current institution's market.
 */
export function getHoursPerWeek(): number {
  return getCurrentCountryConfig().hoursPerWeek;
}

/**
 * Convenience: returns the BCP 47 locale for number formatting (e.g. "da-DK").
 */
export function getNumberLocale(): string {
  return getCurrentCountryConfig().numberLocale;
}

/**
 * Convenience: returns the BCP 47 locale for string collation (e.g. "da").
 */
export function getLocale(): string {
  return getCurrentCountryConfig().locale;
}
